import { Box, Typography } from "@mui/material";
import { NavLink, useMatch } from "react-router-dom";
import {
  AllStudiesIcon,
  AllStudiesIconActive,
  DashboardIcon,
  DashboardIconActive,
  FeaturesIcon,
  FeaturesIconActive,
  HomeIcon,
  HomeIconActive,
  InstrumentLibraryIcon,
  InstrumentLibraryIconActive,
  Logo,
  MahaloIcon,
  ParticipantIcon,
  ParticipantIconActive,
  PlatformUsersIcon,
  PlatformUsersIconActive,
} from "../Common/assets/Sidebar";
import VersionWrapper from "../Common/UI/VersionWrapper";
import { useAppSelector } from "../../Redux/hooks";
import { platformAdminPermissions } from "../../utils/roles";
import { useMemo } from "react";
import SidebarToggle from "./SidebarToggle";
import UserProfileCard from "./UserProfileCard";
import LanguageIcon from "@mui/icons-material/Language";
import LanguageIconActive from "@mui/icons-material/LanguageTwoTone";
// import { setCollapsed } from "../../Redux/reducers/appSlice";

const Sidebar = () => {
  const { platformPermissions } = useAppSelector((state) => state.user);
  const collapsed = useAppSelector((state) => state.app.collapsed);

  const matchFunctions: {
    [key: string]: boolean;
  } = {
    dashboard: !!useMatch("/app/dashboard/*"),
    myStudies: !!useMatch("/app/studies/*"),
    participants: !!useMatch("/app/participants/*"),
    allStudies: !!useMatch("/app/all-studies/*"),
    features: !!useMatch("/app/features/*"),
    platformUsers: !!useMatch("/app/platform-users/*"),
    templates: !!useMatch("/app/templates/*"),
    sites: !!useMatch("/app/sites/*"),
  };

  const rbac = useMemo(
    () => ({
      view: platformPermissions.includes(platformAdminPermissions.viewPlatform),
      managePlatform: platformPermissions.includes(
        platformAdminPermissions.managePlatform
      ),
      createStudy: platformPermissions.includes(
        platformAdminPermissions.createStudy
      ),
      manageUser: platformPermissions.includes(
        platformAdminPermissions.manageUser
      ),
      manageRegistry: platformPermissions.includes(
        platformAdminPermissions.manageRegistry
      ),
    }),
    [platformPermissions]
  );

  return (
    <Box
      className={`sidebar ${collapsed ? "collapsed" : ""}`}
      sx={{
        // "& .active.sider-menu": {
        //   backgroundColor: "primary.main",
        // },
        "& .user-management-menu": {
          color: "text.secondary",
        },
      }}
    >
      <SidebarToggle type="main" />
      <Box>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            paddingBottom: 2,
            margin: "10px 0px",
          }}
        >
          {!collapsed ? <Logo /> : <MahaloIcon />}
        </Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            border: "1px solid #1F438A",
            borderRadius: "10px",
            height: "41px",
            p: 1,
            mb: 1.5,
            width: "100%",
          }}
        >
          <UserProfileCard collapsed={collapsed} />
        </Box>
        {rbac.manageRegistry && (
          <NavLink
            to={`/app/dashboard`}
            title="Dashboard"
            className={({ isActive }) =>
              isActive ? "sider-menu active" : "sider-menu"
            }
          >
            {matchFunctions.dashboard ? (
              <DashboardIconActive />
            ) : (
              <DashboardIcon />
            )}
            {!collapsed && (
              <Typography variant="body2" fontWeight="regular">
                Dashboard
              </Typography>
            )}
          </NavLink>
        )}
        <NavLink
          to="/app/studies"
          title="My Studies"
          className={({ isActive }) =>
            isActive ? "sider-menu active" : "sider-menu"
          }
        >
          {matchFunctions.myStudies ? <HomeIconActive /> : <HomeIcon />}
          {!collapsed && (
            <Typography variant="body2" fontWeight="regular" ml={1}>
              My Studies
            </Typography>
          )}
        </NavLink>
        {rbac.createStudy && (
          <NavLink
            to="/app/all-studies"
            title="All Studies"
            className={({ isActive }) =>
              isActive ? "sider-menu active" : "sider-menu"
            }
          >
            {matchFunctions.allStudies ? (
              <AllStudiesIconActive />
            ) : (
              <AllStudiesIcon />
            )}
            {!collapsed && (
              <Typography variant="body2" fontWeight="regular" ml={1}>
                All Studies
              </Typography>
            )}
          </NavLink>
        )}
        {rbac.manageRegistry && (
          <NavLink
            to="/app/participants"
            title="Participants"
            className={({ isActive }) =>
              isActive ? "sider-menu active" : "sider-menu"
            }
          >
            {matchFunctions.participants ? (
              <ParticipantIconActive />
            ) : (
              <ParticipantIcon />
            )}
            {!collapsed && (
              <Typography variant="body2" fontWeight="regular" ml={1}>
                Participants
              </Typography>
            )}
          </NavLink>
        )}
        {/* <NavLink
          to="/app/organisation"
          title="Organisation"
          className={({ isActive }) =>
            isActive ? "sider-menu active" : "sider-menu"
          }
        >
          <DashboardIcon />
          {!collapsed && (
            <Typography variant="body2" fontWeight="regular">Organisation</Typography>
          )}
        </NavLink> */}
        {rbac?.managePlatform && (
          <NavLink
            to="/app/features"
            title="Features"
            className={({ isActive }) =>
              isActive ? "sider-menu active" : "sider-menu"
            }
          >
            {matchFunctions.features ? (
              <FeaturesIconActive />
            ) : (
              <FeaturesIcon />
            )}
            {!collapsed && (
              <Typography variant="body2" fontWeight="regular" ml={1}>
                Features
              </Typography>
            )}
          </NavLink>
        )}
        {rbac?.manageUser && (
          <NavLink
            to="/app/platform-users"
            title="Users"
            className={({ isActive }) =>
              isActive ? "sider-menu active" : "sider-menu"
            }
          >
            {matchFunctions.platformUsers ? (
              <PlatformUsersIconActive />
            ) : (
              <PlatformUsersIcon />
            )}
            {!collapsed && (
              <Typography variant="body2" fontWeight="regular" ml={1}>
                Users
              </Typography>
            )}
          </NavLink>
        )}
        <NavLink
          to="/app/templates"
          title="Templates"
          className={({ isActive }) =>
            isActive ? "sider-menu active" : "sider-menu"
          }
        >
          {matchFunctions.templates ? (
            <InstrumentLibraryIconActive />
          ) : (
            <InstrumentLibraryIcon />
          )}

          {!collapsed && (
            <Typography variant="body2" fontWeight="regular" ml={1}>
              Templates
            </Typography>
          )}
        </NavLink>
        {rbac.manageRegistry && (
          <NavLink
            to="/app/sites"
            title="Sites"
            className={({ isActive }) =>
              isActive ? "sider-menu active" : "sider-menu"
            }
          >
            {matchFunctions.sites ? <LanguageIconActive /> : <LanguageIcon />}

            {!collapsed && (
              <Typography variant="body2" fontWeight="regular" ml={1}>
                Sites
              </Typography>
            )}
          </NavLink>
        )}
      </Box>
      {/* <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <IconButton onClick={toggleCollapse}>
          {collapsed ? (
            <ChevronRight fontSize="large" />
          ) : (
            <ChevronLeft fontSize="large" />
          )}
        </IconButton>
      </Box> */}
      <VersionWrapper />
    </Box>
  );
};

export default Sidebar;
